import React, { useState } from 'react';
import { X, Check } from 'lucide-react';

// =============================================================================
// Bulk assign — set every picker column on all selected positions at once.
//
// Covers the columns you change by clicking rather than typing: Site, Function,
// Shift, Job Title, Pay Type, Status and Full-time.
//
// Why this rather than pasting into the grid: those columns display a LABEL but
// store a CODE, so a pasted string has to be resolved back — and a function name
// can exist in two modules at the same site, which has no single right answer.
// Picking from the same lists the row pickers use removes the ambiguity, and
// lets the cascade rules be enforced up front.
//
// Cascades, matching the per-row behaviour exactly:
//   site    -> clears function and shift (a function exists at one site only)
//   function-> clears shift              (a crew exists in one function only)
//   title   -> sets grade; on a vacant position also resets basic to grade Mid
//   vacant  -> clears the employee name and resets basic to grade Mid
//
// Every field is three-way: leave unchanged / clear / set to a value.
// =============================================================================

const UNCHANGED = '';
const CLEAR = '__clear__';
const keyOf = (m, f) => `${m}|${f}`;

const BulkAssignModal = ({
  count,
  sites = [],
  functions = [],
  shifts = [],
  jobTitles = [],
  commonSite = null,   // the site all selected rows share, or null if mixed
  commonFn = null,     // { module_code, function_code } shared by all, or null
  onApply,
  onClose,
}) => {
  const [siteSel, setSiteSel] = useState(UNCHANGED);
  const [fnSel, setFnSel] = useState(UNCHANGED);
  const [shiftSel, setShiftSel] = useState(UNCHANGED);
  const [titleSel, setTitleSel] = useState(UNCHANGED);
  const [paySel, setPaySel] = useState(UNCHANGED);
  const [ftSel, setFtSel] = useState(UNCHANGED);
  const [statusSel, setStatusSel] = useState(UNCHANGED);

  // Moving the rows to a new site invalidates whatever function they hold, so
  // the function list follows the chosen site — falling back to the rows' own
  // site only while the site is left unchanged.
  const targetSite = siteSel || commonSite || '';
  const fnOptions = targetSite ? functions.filter((f) => f.site_code === targetSite) : [];

  // Same again one level down: only inherit the rows' current function while
  // neither the site nor the function is being changed.
  const inheritedFnKey = (!siteSel && commonFn)
    ? keyOf(commonFn.module_code, commonFn.function_code) : '';
  const targetFnKey = (fnSel && fnSel !== CLEAR) ? fnSel : (fnSel === CLEAR ? '' : inheritedFnKey);
  const [tMod, tFn] = targetFnKey.split('|');
  const shiftOptions = (targetSite && tFn)
    ? shifts.filter((s) => s.site_code === targetSite && s.module_code === tMod && s.function_code === tFn)
    : [];

  const canApply = [siteSel, fnSel, shiftSel, titleSel, paySel, ftSel, statusSel]
    .some((v) => v !== UNCHANGED);

  const submit = () => {
    if (!canApply) return;
    onApply({
      // undefined = leave alone, null = clear, value = set
      site_code: siteSel || undefined,
      fn: fnSel === CLEAR ? null
        : fnSel ? { module_code: fnSel.split('|')[0], function_code: fnSel.split('|')[1] }
        : undefined,
      shift_no: shiftSel === CLEAR ? null : shiftSel ? Number(shiftSel) : undefined,
      title: titleSel || undefined,
      pay_type: paySel || undefined,
      fulltime: ftSel === UNCHANGED ? undefined : ftSel === 'true',
      is_vacant: statusSel === UNCHANGED ? undefined : statusSel === 'vacant',
    });
  };

  const field = 'w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50';
  const label = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
  const legend = 'm-0 mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400';
  const note = 'm-0 mt-1 text-[11px] text-slate-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md max-h-full overflow-y-auto rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="sticky top-0 z-10 px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-center justify-between">
          <div>
            <h3 className="m-0 text-base font-semibold text-slate-900 dark:text-slate-100">
              Assign {count} position{count === 1 ? '' : 's'}
            </h3>
            <p className="m-0 mt-0.5 text-xs text-slate-400">Anything left unchanged is not touched.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* ---- Labour assignment: site -> function -> shift ---- */}
          <section className="space-y-4">
            <p className={legend}>Labour assignment</p>

            <div>
              <label className={label} htmlFor="bulk-site">Site</label>
              <select id="bulk-site" value={siteSel} className={field}
                onChange={(e) => { setSiteSel(e.target.value); setFnSel(UNCHANGED); setShiftSel(UNCHANGED); }}>
                <option value={UNCHANGED}>— leave unchanged —</option>
                {sites.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>
              {siteSel && (
                <p className="m-0 mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                  Moving sites clears each position&rsquo;s function and shift unless you set new ones below.
                </p>
              )}
            </div>

            <div>
              <label className={label} htmlFor="bulk-fn">Function</label>
              <select id="bulk-fn" value={fnSel} disabled={!targetSite} className={field}
                onChange={(e) => { setFnSel(e.target.value); setShiftSel(UNCHANGED); }}>
                <option value={UNCHANGED}>— leave unchanged —</option>
                <option value={CLEAR}>— clear —</option>
                {fnOptions.map((f) => (
                  <option key={keyOf(f.module_code, f.function_code)} value={keyOf(f.module_code, f.function_code)}>
                    {f.function_name} · {f.module_name || f.module_code}
                  </option>
                ))}
              </select>
              {!targetSite && (
                <p className={note}>The selected positions are at different sites — choose a site above first.</p>
              )}
              {targetSite && fnOptions.length === 0 && (
                <p className={note}>No functions at that site yet — add them in Distribution or Manufacturing › Variable Labour.</p>
              )}
            </div>

            <div>
              <label className={label} htmlFor="bulk-shift">Shift</label>
              <select id="bulk-shift" value={shiftSel} disabled={!tFn} className={field}
                onChange={(e) => setShiftSel(e.target.value)}>
                <option value={UNCHANGED}>— leave unchanged —</option>
                <option value={CLEAR}>— clear —</option>
                {shiftOptions.map((s) => <option key={s.shift_no} value={s.shift_no}>{s.shift_name}</option>)}
              </select>
              {!tFn && <p className={note}>Pick a function first — crews belong to one function.</p>}
              {tFn && shiftOptions.length === 0 && <p className={note}>That function has no named crews yet.</p>}
            </div>
          </section>

          {/* ---- Position details ---- */}
          <section className="space-y-4 pt-1 border-t border-slate-200 dark:border-slate-700">
            <p className={`${legend} pt-4`}>Position details</p>

            <div>
              <label className={label} htmlFor="bulk-title">Job title</label>
              <select id="bulk-title" value={titleSel} className={field}
                onChange={(e) => setTitleSel(e.target.value)}>
                <option value={UNCHANGED}>— leave unchanged —</option>
                {jobTitles.map((j) => (
                  <option key={j.title} value={j.title}>{j.title}{j.grade_code ? ` · ${j.grade_code}` : ''}</option>
                ))}
              </select>
              {titleSel && (
                <p className={note}>Grade follows the title. Vacant positions also get their basic reset to the grade Mid.</p>
              )}
              {jobTitles.length === 0 && (
                <p className={note}>No job titles yet — add them in People › Job Titles.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label} htmlFor="bulk-pay">Pay type</label>
                <select id="bulk-pay" value={paySel} className={field}
                  onChange={(e) => setPaySel(e.target.value)}>
                  <option value={UNCHANGED}>— leave unchanged —</option>
                  <option value="Fixed">Fixed</option>
                  <option value="Variable">Variable</option>
                </select>
              </div>
              <div>
                <label className={label} htmlFor="bulk-ft">Full-time</label>
                <select id="bulk-ft" value={ftSel} className={field}
                  onChange={(e) => setFtSel(e.target.value)}>
                  <option value={UNCHANGED}>— leave unchanged —</option>
                  <option value="true">Full-time</option>
                  <option value="false">Part-time</option>
                </select>
              </div>
            </div>

            <div>
              <label className={label} htmlFor="bulk-status">Status</label>
              <select id="bulk-status" value={statusSel} className={field}
                onChange={(e) => setStatusSel(e.target.value)}>
                <option value={UNCHANGED}>— leave unchanged —</option>
                <option value="filled">Filled</option>
                <option value="vacant">Vacant</option>
              </select>
              {statusSel === 'vacant' && (
                <p className="m-0 mt-1 text-[11px] text-rose-600 dark:text-rose-400">
                  Marking positions vacant clears the employee name and resets basic to the grade Mid.
                  Positions already vacant are left alone.
                </p>
              )}
              {statusSel === 'filled' && (
                <p className={note}>Marks the positions filled — you still need to enter each name.</p>
              )}
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <button type="button" onClick={onClose}
            className="px-3 py-2 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canApply}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed">
            <Check className="w-3.5 h-3.5" /> Apply to {count}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModal;
